import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/db8d7gk6oi3g3tv8ulig';

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const usageSteps = [
  { icon: 'ri-compass-3-line', title: '浏览与检索', desc: '通过首页分类入口、分类页筛选或顶部搜索，快速定位想看的影片。' },
  { icon: 'ri-file-list-3-line', title: '查看详情', desc: '在详情页了解导演、主演、类型、年代等完整资料与剧情简介。' },
  { icon: 'ri-bookmark-line', title: '收藏与分享', desc: '收藏状态保存在本地浏览器，随时分享链接给好友一同观影。' },
];

export default function AboutPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [feedback, setFeedback] = useState('');

  useSeo({
    title: '关于我们 - 香蕉视频app',
    description: '了解香蕉视频app 的平台定位、服务宗旨与联系方式，查看使用说明与免责声明，欢迎通过反馈表单与我们联系。',
    keywords: '关于香蕉视频app,影视平台介绍,联系方式,免责声明,用户反馈',
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFeedback('提交成功！感谢您的反馈，我们会尽快查看并处理。');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFeedback('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const str = String(value).trim();
      if (str) payload.append(key, str);
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setFeedback('提交成功！感谢您的反馈，我们会尽快查看并处理。');
        form.reset();
      } else {
        setStatus('error');
        setFeedback(serverMsg ? String(serverMsg) : '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFeedback('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-8 pt-8 md:pt-12">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-300">关于我们</span>
        </nav>

        <div className="relative rounded-xl overflow-hidden border border-background-200">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20dark%20cinematic%20banner%20with%20abstract%20film%20reels%2C%20projector%20light%20beams%20and%20warm%20yellow%20orange%20glow%20over%20a%20deep%20charcoal%20background%2C%20premium%20editorial%20style%2C%20no%20text&width=1600&height=600&seq=banana-about-01&orientation=landscape"
            alt="香蕉视频app 品牌氛围图"
            title="关于香蕉视频app"
            className="w-full h-[200px] md:h-[280px] object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/20"></div>
          <div className="absolute inset-0 flex items-end px-6 md:px-12 pb-6 md:pb-10">
            <div>
              <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-50">关于香蕉视频app</h1>
              <p className="mt-2 text-sm md:text-base text-foreground-300 max-w-2xl">
                高清影视视频播放平台 · 让每一次观影决策都更简单
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10">
        <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
            <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-50">平台定位</h2>
          </div>
          <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
            香蕉视频app 是一个面向广泛影视爱好者的高清影视信息平台。我们聚合电影与电视剧的丰富资料，通过清晰的分类、多维筛选与高效搜索，
            帮助用户以最短的时间找到想看的内容，提升观影决策效率。
          </p>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="rounded-md bg-background-50 border border-background-200 p-4">
              <p className="font-heading font-extrabold text-xl text-primary-400">12000+</p>
              <p className="text-xs text-foreground-500 mt-1">影视资源条目</p>
            </div>
            <div className="rounded-md bg-background-50 border border-background-200 p-4">
              <p className="font-heading font-extrabold text-xl text-accent-400">每日更新</p>
              <p className="text-xs text-foreground-500 mt-1">跟进最新影视</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
            <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-50">发展愿景</h2>
          </div>
          <p className="text-sm md:text-base text-foreground-300 leading-relaxed">
            我们希望成为用户最信赖的影视发现入口——不只是「找到片子」，更是「找到适合自己的那一部」。
            未来将持续丰富片库、优化推荐与筛选体验，并逐步完善播放能力，让从发现到观看的路径更加顺畅。
          </p>
          <ul className="mt-6 space-y-3 text-sm text-foreground-300">
            <li className="flex items-start gap-2">
              <i className="ri-check-line text-primary-400 mt-0.5"></i>更丰富的高清影视资源库
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-check-line text-primary-400 mt-0.5"></i>更聪明的个性化推荐
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-check-line text-primary-400 mt-0.5"></i>更流畅的多终端观看体验
            </li>
          </ul>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-4">
        <div className="flex items-center gap-3 mb-5">
          <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
          <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-50">使用说明</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-5">
          {usageSteps.map((step, index) => (
            <div key={step.title} className="rounded-lg border border-background-200 bg-background-100 p-5">
              <div className="flex items-center justify-between">
                <span className="w-11 h-11 rounded-lg bg-primary-950/60 text-primary-400 flex items-center justify-center">
                  <i className={`${step.icon} text-xl`}></i>
                </span>
                <span className="font-heading font-extrabold text-2xl text-background-300">
                  {String(index + 1).padStart(2, '0')}
                </span>
              </div>
              <h3 className="mt-4 font-semibold text-foreground-100">{step.title}</h3>
              <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6 md:gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
            <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-50">联系方式</h2>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-lg bg-primary-950/60 text-primary-400 flex items-center justify-center shrink-0">
                <i className="ri-mail-line text-lg"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">客服邮箱</p>
                <p className="text-sm text-foreground-100 mt-1">support@bananavideo.example</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-lg bg-primary-950/60 text-primary-400 flex items-center justify-center shrink-0">
                <i className="ri-customer-service-2-line text-lg"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">服务时间</p>
                <p className="text-sm text-foreground-100 mt-1">每日 09:00 - 22:00</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-lg bg-primary-950/60 text-primary-400 flex items-center justify-center shrink-0">
                <i className="ri-shield-check-line text-lg"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">版权与合作</p>
                <p className="text-sm text-foreground-100 mt-1">business@bananavideo.example</p>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-accent-800/50 bg-accent-950/50 p-6">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-error-warning-line text-accent-400 text-lg"></i>
              <h3 className="font-heading font-semibold text-foreground-50">免责声明</h3>
            </div>
            <p className="text-sm text-foreground-300 leading-relaxed">
              本站为影视信息展示与观看指导平台，页面内所有影视资料均为演示用虚构数据，不提供任何真实影视的在线播放与下载服务。
              播放功能待接入，相关操作仅作界面演示。如涉及版权问题，请通过上方邮箱与我们联系。
            </p>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
            <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-50">意见反馈</h2>
          </div>

          <form
            id="banana-feedback-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8 space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="fb-name" className="block text-sm text-foreground-300 mb-2">您的称呼</label>
                <input
                  id="fb-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入称呼"
                  className="w-full h-11 rounded-md bg-background-50 border border-background-300 px-3 text-sm text-foreground-100 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="fb-email" className="block text-sm text-foreground-300 mb-2">联系邮箱</label>
                <input
                  id="fb-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full h-11 rounded-md bg-background-50 border border-background-300 px-3 text-sm text-foreground-100 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
                />
              </div>
            </div>

            <div>
              <label htmlFor="fb-topic" className="block text-sm text-foreground-300 mb-2">反馈类型</label>
              <select
                id="fb-topic"
                name="topic"
                defaultValue="内容建议"
                className="w-full h-11 rounded-md bg-background-50 border border-background-300 px-3 text-sm text-foreground-100 outline-none focus:border-primary-400 transition-colors cursor-pointer"
              >
                <option value="内容建议">内容建议</option>
                <option value="资源请求">资源请求</option>
                <option value="播放问题">播放问题</option>
                <option value="版权与合作">版权与合作</option>
                <option value="其他">其他</option>
              </select>
            </div>

            <div>
              <label htmlFor="fb-message" className="block text-sm text-foreground-300 mb-2">反馈内容</label>
              <textarea
                id="fb-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请输入您的意见或建议（最多 500 字）"
                className="w-full rounded-md bg-background-50 border border-background-300 px-3 py-3 text-sm text-foreground-100 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors resize-none"
              ></textarea>
            </div>

            <div className="feedback-extra-field" aria-hidden="true">
              <label htmlFor="fb-website-alt">网站备用</label>
              <input
                id="fb-website-alt"
                name="website_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                readOnly
              />
            </div>

            {status !== 'idle' && status !== 'submitting' ? (
              <div
                className={`flex items-start gap-2 rounded-md px-4 py-3 text-sm ${
                  status === 'success'
                    ? 'bg-primary-950/50 border border-primary-800/50 text-primary-200'
                    : 'bg-accent-950/50 border border-accent-800/50 text-accent-200'
                }`}
                role="status"
              >
                <i className={status === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
                <span>{feedback}</span>
              </div>
            ) : null}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <i className="ri-send-plane-fill"></i>
              {status === 'submitting' ? '提交中...' : '提交反馈'}
            </button>
            <p className="text-xs text-foreground-500">提交后我们会在 1-2 个工作日内处理，感谢您的支持。</p>
          </form>
        </div>
      </section>
    </div>
  );
}